import type { SummaryFeedbackVersion } from '@/api/meetingSummaryFeedback'

export const PROMPT_VERSION_RE = /^[0-9a-f]{12}$/
export const MAX_COMPARED = 2
const MAX_MODEL_LENGTH = 200

export type ComparedVersion = { promptVersion: string; model: string }

export const readPromptVersion = (value: string | null | undefined): string | undefined =>
  value && PROMPT_VERSION_RE.test(value) ? value : undefined

export const readModel = (value: string | null | undefined): string | undefined =>
  value && value.length <= MAX_MODEL_LENGTH ? value : undefined

export const promptText = (promptVersion: string, promptLabel: string | null | undefined) =>
  promptLabel ? `${promptLabel} · ${promptVersion}` : promptVersion

export const versionLabel = ({
  promptVersion,
  promptLabel,
  model,
}: Pick<SummaryFeedbackVersion, 'promptVersion' | 'promptLabel' | 'model'>) =>
  `${promptLabel ?? promptVersion ?? 'unknown'} · ${model ?? 'unknown'}`

export const versionKey = ({ promptVersion, model }: ComparedVersion) => `${promptVersion}:${encodeURIComponent(model)}`

export const sameVersion = (a: ComparedVersion, b: ComparedVersion) =>
  a.promptVersion === b.promptVersion && a.model === b.model

const decode = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return undefined
  }
}

export const readCompare = (value: string | null): ComparedVersion[] => {
  const versions: ComparedVersion[] = []
  for (const part of value?.split(',') ?? []) {
    const separator = part.indexOf(':')
    const promptVersion = readPromptVersion(part.slice(0, separator))
    const model = readModel(decode(part.slice(separator + 1)))
    if (separator < 0 || !promptVersion || !model) continue
    const version = { promptVersion, model }
    if (!versions.some((seen) => sameVersion(seen, version))) versions.push(version)
  }
  return versions.slice(0, MAX_COMPARED)
}

export const writeCompare = (versions: ComparedVersion[]) => versions.map(versionKey).join(',') || undefined

export const labelFor = (version: ComparedVersion, known: SummaryFeedbackVersion[]) =>
  versionLabel({
    ...version,
    promptLabel: known.find((row) => row.promptVersion === version.promptVersion)?.promptLabel ?? null,
  })
